import { useThemeStore } from "@stores/theme.store";
import { useMutation, useQuery } from "@tanstack/react-query";
import { db, MarketplaceCart } from "@utils/database";
import { useLiveQuery } from "dexie-react-hooks";
import { useEffect, useState } from "react";
import moment from "moment";
import { useDebounce } from "@utils/debounce";
import { Spiner } from "@components/loaders/spiner";
import { toRp } from "@utils/helper";
import { Button, Checkbox, Form, Input, message, Select, Tag } from "antd";
import TextArea from "antd/es/input/TextArea";
import { NoticeType } from "antd/es/message/interface";
import { MarketplaceService } from "@services/marketplace.service";
import {
  PopupConfirm,
  PopupCustom,
  PopupSuccess,
} from "@components/general/popup";
import { HomeService } from "@services/home.service";
import { SchoolService } from "@services/school.service";
import { Checkout } from "@services/dtos/marketplace.dto";
import { useNavigate } from "react-router-dom";
import { linkPage } from "@utils/router";

const MarketplaceCheckoutPage = () => {
  const maxLengthChar = 200;
  const navigate = useNavigate();
  const { setHeader, setAppBar, setBottomNavigation } = useThemeStore(
    (state) => state,
  );
  const [formCheckout] = Form.useForm();
  // const [formCreatePin] = Form.useForm();
  const [messageApi, contextHolder] = message.useMessage();
  const [showMessage, setShowMessage] = useState<boolean>(false);
  const [showMessageText, setShowMessageText] = useState<string>("");
  const [messageType, setMessageType] = useState<string>("");
  const [cartUpdate, setCartUpdate] = useState<Partial<MarketplaceCart>>({});
  const cartUpdateTerm = useDebounce<Partial<MarketplaceCart>>(cartUpdate, 500);
  const [counter, setCounter] = useState<{ [key: number]: number }>({});
  const [popupConfirmSetPin, setPopupConfirmSetPin] = useState<boolean>(false);
  const [popupSetPin, setPopupSetPin] = useState<boolean>(false);
  const [popupCheckout, setPopupCheckout] = useState<boolean>(false);
  const [popupSuccess, setPopupSuccess] = useState<boolean>(false);
  const [fee, setFee] = useState<number>(1000);

  const carts = useLiveQuery(() => db.carts.toArray());

  const { data: dataNis } = useQuery({
    queryKey: [SchoolService.queries.GET_SCHOOL_STUDENT_BIO],
    queryFn: SchoolService.studentBio,
  });
  const nis = dataNis?.data?.nis ?? "";

  const { data: listCarts, isPending: isPendingListCarts } = useQuery({
    queryKey: [
      MarketplaceService.queries.GET_PRODUCT_READY,
      ...(carts?.map((item) => item.id?.toString() ?? "") ?? []),
    ],
    queryFn: async () => {
      let data = typeof carts === "undefined" ? [] : carts;
      if (data.length === 0) return [];
      let dataDb = await MarketplaceService.productIsReady(
        carts?.map((item) => item.id?.toString() ?? "") ?? [],
      );
      return data.map((item) => {
        return {
          ...item,
          isReady:
            dataDb.data?.find((itemDb) => itemDb.id === item.id?.toString())
              ?.isReady ?? "N",
          timeOpen:
            dataDb.data?.find((itemDb) => itemDb.id === item.id?.toString())
              ?.timeOpen ?? "",
          timeClose:
            dataDb.data?.find((itemDb) => itemDb.id === item.id?.toString())
              ?.timeClose ?? "",
          stok:
            dataDb.data?.find((itemDb) => itemDb.id === item.id?.toString())
              ?.stok ?? 0,
        };
      });
    },
  });

  const { data: dataSaldoUangSaku } = useQuery({
    queryKey: [HomeService.queries.GET_BILLS_AND_BALANCES, nis],
    queryFn: () => HomeService.billsAndBalances({ nis }),
  });
  const saldoUangSaku = dataSaldoUangSaku?.data?.saldo ?? 0;

  const { mutate: checkPin, isPending: isPendingCheckPin } = useMutation({
    mutationFn: MarketplaceService.checkPin,
    onSuccess: (response) => {
      if (response.success) {
        onCheckout();
      } else if (response.errorCode === "E_UNPROCESSABLE_ENTITY") {
        setPopupConfirmSetPin(true);
      } else {
        setShowMessage(true);
        setMessageType("error");
        setShowMessageText(response.message);
      }
    },
    onError: (error: any) => {
      setShowMessage(true);
      setMessageType("error");
      setShowMessageText(error.message ?? "Internal Error");
    },
  });

  const { mutate: createPin, isPending: isPendingCreatePin } = useMutation({
    mutationFn: MarketplaceService.createPin,
    onSuccess: (response) => {
      setShowMessage(true);
      setMessageType(response.success ? "success" : "error");
      setShowMessageText("Berhasil membuat PIN");
      if (response.success) setPopupSetPin(false);
    },
    onError: (error: any) => {
      setShowMessage(true);
      setMessageType("error");
      setShowMessageText(error.message ?? "Internal Error");
    },
  });

  const { mutate, isPending } = useMutation({
    mutationFn: MarketplaceService.checkout,
    onSuccess: (response) => {
      if (response.success) {
        setPopupCheckout(false);
        // setFee(1000);
        setPopupSuccess(true);
        db.carts.where("id").anyOf(response.data).delete();
      } else {
        setShowMessage(true);
        setMessageType("error");
        setShowMessageText(response.message);
      }
    },
    onError: (error: any) => {
      setShowMessage(true);
      setMessageType("error");
      setShowMessageText(error.message ?? "Internal Error");
    },
  });

  useEffect(() => {
    setAppBar({
      title: "Keranjang",
      show: true,
      shopList: true,
      leading: "back",
    });
    setHeader({
      buttonBack: true,
      title: "Keranjang",
      filter: false,
      image: false,
    });
    setBottomNavigation("hidden");
  }, [setHeader, setAppBar, setBottomNavigation]);

  useEffect(() => {
    if (cartUpdateTerm.id) db.carts.update(cartUpdateTerm.id, cartUpdateTerm);
  }, [cartUpdateTerm]);

  useEffect(() => {
    if (!popupCheckout) return; // Only set when modal is open

    const amount =
      carts
        ?.map((item) =>
          listCarts?.map((cart) =>
            item.check && cart.id === item.id && cart.isReady === "Y"
              ? item.price * item.qty
              : 0,
          ),
        )
        .flat()
        .reduce((acc = 0, curr = 0) => acc + curr, 0) ?? 0;

    // Ensure the form is filled when modal opens
    formCheckout.setFieldsValue({ amount: toRp(amount) });
  }, [popupCheckout, carts, listCarts]);

  useEffect(() => {
    if (carts && listCarts && carts.length > 0 && listCarts.length > 0) {
      carts.forEach((cart) => {
        const cacheCart = listCarts.find((item) => item.id === cart.id);
        if (cart.check && cacheCart) {
          if (
            cacheCart.isReady !== "Y" ||
            (cacheCart.isReady === "Y" &&
              (moment(cacheCart.timeOpen, "HH:mm:ss").format("HH:mm:ss") >
                new Date().toLocaleTimeString("en-GB", {
                  hour12: false,
                }) ||
                moment(cacheCart.timeClose, "HH:mm:ss").format("HH:mm:ss") <
                  new Date().toLocaleTimeString("en-GB", {
                    hour12: false,
                  })))
          ) {
            cart.id && db.carts.update(cart.id, { check: false });
          }
        }
      });
    }
  }, [carts, listCarts]);

  useEffect(() => {
    if (showMessage) {
      messageApi.open({
        type: messageType as NoticeType,
        content: <>{showMessageText}</>,
      });
    }
    setShowMessage(false);
  }, [showMessage, showMessageText]);

  const onDeleteCart = async (cart: MarketplaceCart) => {
    if (cart.id) db.carts.delete(cart.id);
    setShowMessage(true);
    setMessageType("success");
    setShowMessageText(`Berhasil hapus produk dari keranjang`);
  };

  const onCheck = async (cart: MarketplaceCart) => {
    if (cart.id) db.carts.update(cart.id, { check: !cart.check });
  };

  const onPayment = async (checkout: Checkout) => {
    if (isPending) return;
    const totalBayar = `${checkout.amount}`.slice(4).replace(/\./g, "");

    if (parseInt(totalBayar) > saldoUangSaku) {
      setShowMessage(true);
      setMessageType("error");
      setShowMessageText(`Jumlah pembayaran melebihi saldo UANG SAKU`);
      return;
    }
    const dataCarts = carts
      ?.filter((item) => item.check)
      .map((item) => ({
        id: Number(item.id ?? 0),
        qty: Number(item.qty ?? 0),
        notes: item.message,
      }));
    mutate({
      pin: `${checkout.pin}`,
      paymentMethod: `${checkout.paymentMethod.split(" ")[0]}`,
      paymentFee: fee,
      carts: dataCarts ?? [],
    });
  };

  const onCheckout = () => {
    const amount =
      carts
        ?.map((item) => (item.check ? item.price * item.qty : 0))
        .reduce((accumulator, currentValue) => {
          return accumulator + currentValue;
        }, 0) ?? 0;
    if (amount <= 0) {
      setShowMessage(true);
      setMessageType("error");
      setShowMessageText("belum ada produk yang dipilih");
      return;
    }
    setPopupCheckout(true);
  };

  return (
    <>
      {contextHolder}
      <div className="tw-px-4 tw-pt-2 tw-pb-20">
        {isPendingListCarts ? (
          <div className="tw-text-center tw-mt-8">
            <Spiner />
          </div>
        ) : (
          <>
            {carts?.map((item, idx) =>
              listCarts?.map(
                (cart, keyCart) =>
                  cart.id === item.id && (
                    <div
                      key={idx}
                      className="tw-mb-4 tw-px-2 tw-py-2 card-body"
                    >
                      <div className="tw-flex">
                        <div className="tw-me-2 tw-w-1/3 tw-relative">
                          <img
                            src={`https://tarshib.sipond.id/assets/mplace/${item.picture}`}
                            alt="product"
                            className="tw-rounded-xl tw-shadow-lg tw-object-cover"
                            width="100"
                            height="100"
                          />
                          {/* {cart.stok <= 10 && (
                            <div className="tw-py-1 tw-text-xs tw-text-center tw-text-white tw-absolute tw-w-[100px] tw-bottom-[5px] tw-bg-[#000000b3] tw-rounded-b-lg">
                              {cart.stok === 0
                                ? "Stok Habis"
                                : `tersisa ${cart.stok}`}
                            </div>
                          )} */}
                        </div>
                        <div className="tw-w-2/3 tw-grid detail-wrapper">
                          <div className="tw-h-[34px] tw-text-text-secondary tw-text-xs tw-line-clamp-2">
                            {item.name}
                          </div>
                          <div className="tw-flex tw-justify-between tw-items-center">
                            <div key={keyCart} className="tw-h-[25px]">
                              {cart.isReady === "Y" &&
                              // cart.stok > 0 &&
                              moment(cart.timeOpen, "HH:mm:ss").format(
                                "HH:mm:ss",
                              ) <
                                new Date().toLocaleTimeString("id-ID", {
                                  hour12: false,
                                }) &&
                              moment(cart.timeClose, "HH:mm:ss").format(
                                "HH:mm:ss",
                              ) >
                                new Date().toLocaleTimeString("id-ID", {
                                  hour12: false,
                                }) ? (
                                <Tag
                                  color="green"
                                  className="tw-text-[10px] tw-uppercase"
                                >
                                  Tersedia
                                </Tag>
                              ) : (
                                <Tag
                                  color="red"
                                  className="tw-text-[10px] tw-uppercase"
                                >
                                  Tidak Tersedia
                                </Tag>
                              )}
                            </div>
                            <div className="tw-me-1">
                              {cart.isReady === "Y" &&
                                // cart.stok > 0 &&
                                moment(cart.timeOpen, "HH:mm:ss").format(
                                  "HH:mm:ss",
                                ) <
                                  new Date().toLocaleTimeString("id-ID", {
                                    hour12: false,
                                  }) &&
                                moment(cart.timeClose, "HH:mm:ss").format(
                                  "HH:mm:ss",
                                ) >
                                  new Date().toLocaleTimeString("id-ID", {
                                    hour12: false,
                                  }) && (
                                  <div
                                    key={keyCart}
                                    className="tw-text-end"
                                    onClick={() => onCheck(item)}
                                  >
                                    <Checkbox
                                      checked={item.check}
                                      id={`check-${item.id}`}
                                    ></Checkbox>
                                  </div>
                                )}
                            </div>
                          </div>

                          <div className="tw-flex tw-justify-between">
                            <div className="tw-my-auto tw-w-2/3 tw-font-semibold tw-text-base tw-text-accent-primary">
                              {toRp(item.price)}
                            </div>
                            <div className="tw-my-auto tw-w-1/3 tw-text-end">
                              <Select
                                className="tw-text-base tw-w-[70px] tw-p-0"
                                placeholder="0"
                                size="small"
                                defaultValue={item.qty}
                                style={{ width: "100%" }}
                                allowClear
                                onChange={(e) =>
                                  setCartUpdate({
                                    id: item.id!,
                                    qty: e,
                                    message: item.message,
                                  })
                                }
                                options={[
                                  ...Array.from({ length: 100 }, (_, i) => ({
                                    value: (i + 1).toString(),
                                    label: `${i + 1}`,
                                  })),
                                ]}
                              ></Select>
                            </div>
                          </div>
                        </div>
                      </div>

                      <div className="tw-flex tw-items-end tw-mt-2">
                        <div className="tw-w-[75%]">
                          <TextArea
                            style={{
                              height: "50px",
                              transition: "height 0.3s ease-in-out",
                            }}
                            onFocus={(e) => {
                              e.currentTarget.style.height = "200px";
                            }}
                            onBlur={(e) => {
                              e.currentTarget.style.height = "50px";
                            }}
                            onChange={(e) => {
                              setCartUpdate({
                                id: item.id!,
                                qty: item.qty,
                                message: e.target.value,
                              });
                              const counter = e.target.value.length;
                              setCounter((prevCounters) => ({
                                ...prevCounters,
                                [idx]: counter,
                              }));
                            }}
                            id={`form-message-${idx}`}
                            // defaultValue={item.message}
                            defaultValue={""}
                            placeholder="masukkan catatan Anda"
                            maxLength={maxLengthChar}
                          ></TextArea>
                          <div className="tw-text-[10px] tw-text-end tw-text-text-secondary">
                            {counter[idx] ?? 0}/{maxLengthChar}
                          </div>
                        </div>
                        <div className="tw-mb-3 tw-w-[25%] tw-text-end">
                          <Button
                            onClick={() => onDeleteCart(item)}
                            className="tw-h-6 tw-rounded-lg tw-text-xs tw-text-white tw-shadow-sm tw-bg-red-500"
                          >
                            Hapus
                          </Button>
                        </div>
                      </div>
                    </div>
                  ),
              ),
            )}
          </>
        )}
      </div>

      <div
        className="tw-py-4 tw-px-8 tw-bg-white tw-flex tw-justify-between tw-items-end"
        style={{
          boxShadow: "0px 0px 3px #00000080",
          margin: "0 auto",
          position: "fixed",
          bottom: "0",
          right: "0",
          left: "0",
          zIndex: "10",
        }}
      >
        <div className="tw-w-1/2">
          <div className="tw-text-xs tw-text-text-secondary">Total Bayar</div>
          <div className="tw-text-xl tw-font-semibold tw-text-accent-primary">
            {toRp(
              carts
                ?.map((item) =>
                  listCarts?.map((cart) =>
                    item.check &&
                    cart.id === item.id &&
                    cart.isReady === "Y" &&
                    moment(cart.timeOpen, "HH:mm:ss").format("HH:mm:ss") <
                      new Date().toLocaleTimeString("en-GB", {
                        hour12: false,
                      }) &&
                    moment(cart.timeClose, "HH:mm:ss").format("HH:mm:ss") >
                      new Date().toLocaleTimeString("en-GB", {
                        hour12: false,
                      })
                      ? item.price * item.qty
                      : 0,
                  ),
                )
                .flat()
                .reduce((accumulator = 0, currentValue = 0) => {
                  return accumulator + currentValue;
                }, 0) ?? 0,
            )}
          </div>
        </div>

        <div className="tw-w-1/2 tw-text-end">
          <Button
            onClick={() => (isPendingCheckPin ? null : checkPin())}
            className="tw-rounded-lg tw-text-base tw-text-white tw-shadow-sm bg-gradient tw-text-background-200 focus:tw-shadow-none hover:tw-bg-background-200 hover:tw-text-accent-primary hover:tw-border-primary"
          >
            Checkout
          </Button>
        </div>
      </div>

      <PopupConfirm
        open={popupConfirmSetPin}
        title="PIN belum dibuat"
        message="Anda belum membuat PIN transaksi, buat PIN transaksi sekarang?"
        textConfirm="Ya, Buat"
        textCancel="Kembali"
        onConfirm={() => {
          setPopupSetPin(true);
          setPopupConfirmSetPin(false);
        }}
        onCancel={() => setPopupConfirmSetPin(false)}
      />

      <PopupCustom
        title="Buat PIN Transaksi"
        open={popupSetPin}
        onCancel={() => setPopupSetPin(false)}
        slideUpModal={true}
      >
        <Form
          // form={formCreatePin}
          autoComplete="off"
          onFinish={(data) => {
            createPin(data.pin);
          }}
        >
          <Form.Item
            name="pin"
            label="PIN"
            rules={[
              {
                // required: true,
                message: "Masukkan PIIN Transaksi Anda!",
              },
            ]}
          >
            <Input.Password />
          </Form.Item>

          <Form.Item
            name="confirm"
            label="Konfirmasi PIN"
            dependencies={["pin"]}
            hasFeedback
            rules={[
              {
                // required: true,
                message: "Konfirmasi PIN Transaksi Anda!",
              },
              ({ getFieldValue }) => ({
                validator(_, value) {
                  if (!value || getFieldValue("pin") === value) {
                    return Promise.resolve();
                  }
                  return Promise.reject(
                    new Error("PIN Transaksi tidak cocok!"),
                  );
                },
              }),
            ]}
          >
            <Input.Password />
          </Form.Item>

          <Button
            type="primary"
            htmlType="submit"
            loading={isPendingCreatePin}
            className="tw-p-2 tw-mt-6 tw-h-10 tw-w-full tw-text-base tw-rounded-xl bg-gradient tw-text-background-200 focus:tw-shadow-none hover:tw-bg-background-200 hover:tw-text-accent-primary hover:tw-border-primary"
          >
            {isPendingCheckPin ? "" : "Buat PIN Transaksi"}
          </Button>
        </Form>
      </PopupCustom>

      <PopupCustom
        title="Konfirmasi Pembayaran"
        open={popupCheckout}
        onCancel={() => setPopupCheckout(false)}
        slideUpModal={true}
      >
        <Form
          form={formCheckout}
          autoComplete="off"
          onFinish={(data) => onPayment(data)}
          initialValues={{
            paymentMethod: `UANGSAKU ${toRp(saldoUangSaku)}`,
            pin: "",
            paymentFee: toRp(fee),
          }}
        >
          <Form.Item
            className="tw-mb-4"
            label="Bayar dari"
            name="paymentMethod"
          >
            <Input name="paymentMethod" disabled={true} />
          </Form.Item>

          <Form.Item className="tw-mb-4" label="Total pesanan" name="amount">
            <Input name="amount" disabled={true} />
          </Form.Item>

          <Form.Item
            className="tw-mb-4"
            label="Biaya layanan"
            name="paymentFee"
          >
            <Input name="paymentFee" disabled={true} />
          </Form.Item>

          <Form.Item
            className="tw-mb-4"
            name="pin"
            label="PIN"
            rules={[
              {
                required: true,
                message: "Masukkan PIIN Transaksi Anda!",
              },
            ]}
          >
            <Input.Password />
          </Form.Item>
          <Button
            type="primary"
            htmlType="submit"
            loading={isPending}
            className="tw-p-2 tw-mt-6 tw-h-10 tw-w-full tw-text-base tw-rounded-xl bg-gradient tw-text-background-200 focus:tw-shadow-none hover:tw-bg-background-200 hover:tw-text-accent-primary hover:tw-border-primary"
          >
            {isPending
              ? ""
              : `Total Bayar (${toRp(
                  (carts
                    ?.map((item) =>
                      listCarts?.map((cart) =>
                        item.check &&
                        cart.id === item.id &&
                        cart.isReady === "Y"
                          ? item.price * item.qty
                          : 0,
                      ),
                    )
                    .flat()
                    .reduce((accumulator = 0, currentValue = 0) => {
                      return accumulator + currentValue;
                    }, 0) ?? 0) + fee,
                )})`}
          </Button>
        </Form>
      </PopupCustom>

      <PopupSuccess
        open={popupSuccess}
        title="Berhasil!"
        message="Pesanan berhasil dibuat, silahkan lihat daftar pesanan!"
        textConfirm="Lihat Pesanan"
        onConfirm={() => navigate(linkPage.MARKETPLACE_HISTORY)}
      />
    </>
  );
};

export default MarketplaceCheckoutPage;
